import { TypographyProps } from '@global-engineering-shared/gweb-material-types';
import { Box, Typography } from '@global-engineering-shared/gweb-material-ui';
import clsx from 'clsx';

import EntityDetailValue from 'features/EntityDetails/components/EntityDetails/EntityDetail/components';
import useStyles from 'features/EntityDetails/components/EntityDetails/EntityDetail/EntityDetail.styles';
import { getSafeDetailValue } from 'utils/getNullSafeValue';
import { truncateText } from 'utils/truncateText';

type TEntityDetailValueType = string | number | boolean | null;

type TEntityDetailMultiValueType = string[] | null;

export interface IEntityDetailProps {
  label: string;
  value?: TEntityDetailValueType;
  multiValue?: TEntityDetailMultiValueType;
  previousValue?: TEntityDetailValueType;
  previousMultiValue?: TEntityDetailMultiValueType;
  layout?: 'inline' | 'stacked';
  variant?: TypographyProps['variant'];
  showLabel?: boolean;
}

const EntityDetail = ({
  label,
  value,
  multiValue,
  previousValue,
  previousMultiValue,
  layout = 'inline',
  variant = 'body2',
  showLabel = true,
}: IEntityDetailProps) => {
  const classes = useStyles();
  const containerClass = clsx({
    [classes.inline]: layout === 'inline',
    [classes.stacked]: layout === 'stacked',
  });

  const numberOfValues = Math.max(multiValue?.length || 0, previousMultiValue?.length || 0);

  const renderLabel = () =>
    showLabel && (
      <Typography
        data-testid={`${layout}-label`}
        variant={variant}
        color={layout === 'stacked' ? 'textSecondary' : 'textPrimary'}
      >
        {layout === 'inline' ? `${label}: ` : label}
      </Typography>
    );

  const truncateSafeValue = (val: TEntityDetailValueType | undefined): string => {
    return truncateText(getSafeDetailValue(val), 60);
  };

  return (
    <div className={containerClass}>
      {renderLabel()}
      <Box className={classes.multiValueContainer}>
        {multiValue || previousMultiValue ? (
          Array.from({ length: numberOfValues }, (_, index) => (
            <EntityDetailValue
              key={index}
              label={label}
              value={truncateSafeValue(multiValue?.[index])}
              previousValue={truncateSafeValue(previousMultiValue?.[index])}
              variant={variant}
            />
          ))
        ) : (
          <EntityDetailValue
            label={label}
            value={truncateSafeValue(value)}
            previousValue={truncateSafeValue(previousValue)}
            variant={variant}
          />
        )}
      </Box>
    </div>
  );
};

export default EntityDetail;
